import { memo, useCallback, useContext, useMemo, useState } from 'react';

import RenderRichText from '@/components/RenderRichText';
import { Color } from '@/constants/color';
import DisqualificationForm from '@/forms/DisqualificationForm';
import PersonContext from '@/pages/Person/PersonContext';
import ProgramQualificationEditor from '@/pages/Profile/components/ProgramQualificationSection/components/ProgramQualificationEditor/ProgramQualificationEditor';
import personNeedsNewProgramQualification from '@/pages/Profile/utils/personNeedsNewProgramQualification';
import ProgramRepository from '@/repositories/ProgramRepository';
import { EpisodeOfCare } from '@/types/episodeOfCare';
import { ProgramQualification } from '@/types/person';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import formatStaffName from '@/utils/formatStaffName';

const ProgramQualificationSection = () => {
  const [creatingProgramQualification, setCreatingProgramQualification] = useState(false);
  const [disqualifying, setDisqualifying] = useState(false);
  const programRepository = ProgramRepository();
  const {
    person,
    person: { episode_of_care, id: personId, program_qualification },
    refetch: refetchPerson
  } = useContext(PersonContext);
  const [fetchingPersonData, setFetchingPersonData] = useState(false);

  const createProgramQualification = useCallback(async () => {
    setCreatingProgramQualification(true);
    const newProgramQualification = await programRepository.createProgramQualification({
      params: {
        person_id: person.id,
        primary_substance: program_qualification?.primary_substance ?? null,
        _program_id: program_qualification?.selected_program?.id ?? null
      }
    });
    setCreatingProgramQualification(false);

    return newProgramQualification;
  }, [
    person.id,
    program_qualification?.primary_substance,
    program_qualification?.selected_program?.id,
    programRepository
  ]);

  const needsToCreateNewProgramQualification = useMemo(() => {
    return personNeedsNewProgramQualification(person);
  }, [person]);

  const refetchPersonData = useCallback(async () => {
    setFetchingPersonData(true);
    await refetchPerson();
    setFetchingPersonData(false);
  }, [refetchPerson]);

  const handleDisqualifyClick = useCallback(async () => {
    if (needsToCreateNewProgramQualification) {
      await createProgramQualification();
    }
    // Always refetch to ensure fresh data
    await refetchPersonData();
    setDisqualifying(true);
  }, [createProgramQualification, needsToCreateNewProgramQualification, refetchPersonData]);

  const handleCreateProgramQualificationClick = useCallback(async () => {
    await createProgramQualification();
    await refetchPersonData();
  }, [createProgramQualification, refetchPersonData]);

  const disableDisqualifyButton = creatingProgramQualification || fetchingPersonData;

  const renderDisqualify = useMemo(() => {
    if (canDisqualify(program_qualification, episode_of_care)) {
      if (disqualifying && program_qualification) {
        return (
          <DisqualificationForm
            onCancel={() => setDisqualifying(false)}
            onSuccess={() => setDisqualifying(false)}
            personId={personId}
            programQualificationId={program_qualification.id}
          />
        );
      }

      return (
        <Button
          className="my-1"
          color={Color.RED}
          disabled={disableDisqualifyButton}
          loading={disableDisqualifyButton}
          onClick={() => handleDisqualifyClick()}
          size="sm">
          {disableDisqualifyButton ? <Spinner /> : 'Disqualify'}
        </Button>
      );
    }

    return null;
  }, [
    disableDisqualifyButton,
    disqualifying,
    episode_of_care,
    handleDisqualifyClick,
    personId,
    program_qualification
  ]);

  const renderCreateProgramQualificationButton = useMemo(() => {
    const isNotCurrentlyEnrolled = !episode_of_care || episode_of_care?.status !== 'active';
    if (!program_qualification || (program_qualification.ended_at && isNotCurrentlyEnrolled)) {
      const disableCreatePQButton = creatingProgramQualification || fetchingPersonData;
      return (
        <Button
          className="my-1"
          color={Color.BLUE}
          disabled={disableCreatePQButton}
          loading={disableCreatePQButton}
          onClick={handleCreateProgramQualificationClick}
          size="sm">
          {disableCreatePQButton ? <Spinner /> : 'Create Program Qualification'}
        </Button>
      );
    }

    return null;
  }, [
    creatingProgramQualification,
    episode_of_care,
    fetchingPersonData,
    handleCreateProgramQualificationClick,
    program_qualification
  ]);

  const hasIntakeRejectionReasons = program_qualification?.intake_rejection_reasons?.length;
  const hasDisqualificationReasons = program_qualification?.disqualification_reasons?.length;

  return (
    <div id="program-qualification-section" className="relative">
      <Heading value={3} text="Program Qualification" />

      <div className="flex space-x-1">
        {renderDisqualify}
        {renderCreateProgramQualificationButton}
      </div>

      {!program_qualification && <p>No Program Qualification</p>}

      {program_qualification?.rejected_at && (
        <DataList>
          <DataRow label="Rejected At">
            <DualTime dateTime={new Date(program_qualification.rejected_at)} />
          </DataRow>
          <DataRow label="Rejected By">
            {program_qualification.rejected_by
              ? formatStaffName({ staff: program_qualification.rejected_by })
              : 'No record'}
          </DataRow>

          {hasIntakeRejectionReasons ? (
            <DataRow label="Rejection Reasons">
              {program_qualification?.intake_rejection_reasons?.map((reason, index) => {
                return <div key={`${reason}-${index}`}>{reason}</div>;
              })}
            </DataRow>
          ) : null}

          {hasDisqualificationReasons ? (
            <DataRow label="Disqualification Reasons">
              {program_qualification?.disqualification_reasons?.map((reason, index) => {
                return <div key={`${reason}-${index}`}>{reason}</div>;
              })}
            </DataRow>
          ) : null}

          {program_qualification?.intake_rejection_note && (
            <DataRow label="Rejection Note">
              <RenderRichText body={program_qualification?.intake_rejection_note} />
            </DataRow>
          )}
        </DataList>
      )}

      {program_qualification && (
        <div className="relative">
          <ProgramQualificationEditor />
        </div>
      )}
    </div>
  );
};

export function canDisqualify(
  program_qualification: ProgramQualification | null | undefined,
  episode_of_care: EpisodeOfCare | null
): boolean {
  // Cannot Disqualify if currently enrolled
  if (episode_of_care && !episode_of_care.discharged_at) {
    return false;
  }

  // Can Disqualify if no PQ exists
  if (!program_qualification) {
    return true;
  }

  // Cannot Disqualify if PQ is rejected
  if (program_qualification.rejected_at) {
    return false;
  }

  // Otherwise, can Disqualify
  return true;
}

export default memo(ProgramQualificationSection);
